import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { useCallback, useEffect, useState } from 'react';
import {
  ActivityIndicator,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  View,
} from 'react-native';
import { mvs } from 'react-native-size-matters';

import { getPodcastEpisodeFromEpisodeId } from '../../api/mobileApi';
import { useAppSelector } from '../../app/hooks';
import { CustomIcon } from '../../components/CustomIcon';
import CustomText from '../../components/CustomText';
import {
  EpisodeControlsSection,
  EpisodeHeader,
  EpisodeHeaderNav,
  EpisodeHeaderTablet,
  EpisodeMenu,
  HTMLContentRenderer,
} from '../../components/Podcast';
import { ScreenLayout } from '../../components/ScreenLayout';
import { COLORS, IS_WEB, isTablet } from '../../constants';
import { selectDivRadius } from '../../features/appconfig/store/selectors';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';
import i18n from '../../localization/i18n';
import { PodcastStackNavigatorParams } from '../../navigation/PodcastStackNavigator';

type PodcastEpisodeScreenProps = NativeStackScreenProps<
  PodcastStackNavigatorParams,
  'PodcastEpisode'
>;

export default function PodcastEpisodeScreen({
  navigation,
  route,
}: PodcastEpisodeScreenProps) {
  const { episode, tabName, podcastId, episodeId } = route.params;

  // menu state
  const [menuVisible, setMenuVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [episodeState, setEpisodeState] = useState(episode);
  const divRadius = useAppSelector(selectDivRadius);

  // applies custom margin config
  const { bodyHorizontalPadding } = useResponsiveLayout();

  const handleBackPress = useCallback(() => {
    navigation.goBack();
  }, [navigation]);

  const handleMenuPress = useCallback(() => {
    if (IS_WEB) {
      return;
    }

    setMenuVisible(true);
  }, []);

  const handleMenuClose = useCallback(() => {
    if (IS_WEB) {
      return;
    }

    setMenuVisible(false);
  }, []);

  const handlePodcastPress = useCallback(() => {
    if (episodeState?.podcastId) {
      navigation.navigate('PodcastFeed', { podcastUrl: episodeState.podcastUrl });
    }
  }, [navigation, episodeState]);

  useEffect(() => {
    if (!episodeState && tabName && podcastId && episodeId) {
      getPodcastEpisodeFromEpisodeId(tabName, podcastId, episodeId)
        .then(result => {
          setEpisodeState(result);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [episodeId, episodeState, podcastId, tabName]);

  if (isLoading && !episode) {
    return (
      <ScreenLayout
        isPodcastModule
        headerContents={
          <View style={styles.header}>
            <TouchableOpacity onPress={handleBackPress}>
              <CustomIcon name="back" size={24} color={COLORS.WHITE} />
            </TouchableOpacity>
          </View>
        }
      >
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={COLORS.WHITE} />
        </View>
      </ScreenLayout>
    );
  } else if (!episodeState) {
    return (
      <ScreenLayout
        isPodcastModule
        headerContents={
          <View style={styles.header}>
            <TouchableOpacity onPress={handleBackPress}>
              <CustomIcon name="back" size={24} color={COLORS.WHITE} />
            </TouchableOpacity>
          </View>
        }
      >
        <View style={styles.errorContainer}>
          <CustomText style={styles.errorText}>
            {i18n.t('podcast.episodeNotFound')}
          </CustomText>
        </View>
      </ScreenLayout>
    );
  }

  return (
    <ScreenLayout
      isPodcastModule
      headerContents={
        <EpisodeHeaderNav
          podcastThumbnail={episodeState.podcastLogo}
          podcastName={episodeState.podcastTitle}
          onBackPress={handleBackPress}
          onMenuPress={handleMenuPress}
          onPodcastPress={handlePodcastPress}
        />
      }
    >
      <ScrollView
        style={[styles.container, { paddingHorizontal: bodyHorizontalPadding }]}
        showsVerticalScrollIndicator={false}
      >
        {isTablet ? (
          <>
            {/* tablet header */}
            <EpisodeHeaderTablet episode={episodeState} />
          </>
        ) : (
          <>
            {/* mobile header */}
            <View style={styles.episodeHeaderContainer}>
              <EpisodeHeader
                episodeImage={episodeState.image}
                episodeTitle={episodeState.title}
              />
            </View>

            {/* mobile episode controls */}
            <EpisodeControlsSection {...episodeState} />
          </>
        )}
        {/* episode content */}
        <View style={[styles.episodeContentContainer, { borderRadius: divRadius }]}>
          <HTMLContentRenderer content={episodeState.content} />
        </View>
      </ScrollView>

      {/* episode menu */}
      <EpisodeMenu
        visible={menuVisible}
        onRequestClose={handleMenuClose}
        handleViewPodcastPage={handlePodcastPress}
        episode={episodeState}
      />
    </ScreenLayout>
  );
}

const styles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  container: {
    flex: 1,
  },
  episodeHeaderContainer: {
    marginBottom: mvs(42),
  },
  episodeContentContainer: {
    width: '100%',
    backgroundColor: COLORS.WHITE,
    padding: mvs(24),
    marginBottom: mvs(24),
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorText: {
    fontSize: mvs(16),
    color: COLORS.WHITE,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
});
